import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Beaker, Box, Package } from "lucide-react";
import { toast } from "sonner";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { useCreatePurchaseOrder } from "~/hooks/purchasing";
import type { OrderType, PurchaseOrderFormData } from "~/types/purchasing";

import { PurchaseOrderForm } from "./components/PurchaseOrderForm";

export function NewPurchaseOrderPage() {
  const navigate = useNavigate();
  const createOrder = useCreatePurchaseOrder();
  const [orderType, setOrderType] = useState<OrderType | null>(null);

  const handleSubmit = async (data: PurchaseOrderFormData) => {
    try {
      const newOrder = await createOrder.mutateAsync({
        ...data,
        order_type: orderType!,
      });
      toast.success("Orden de compra creada");
      navigate(`/purchasing/orders/${newOrder.id}`);
    } catch (error) {
      toast.error("Error al crear la orden de compra");
    }
  };

  // Order type selection step
  if (!orderType) {
    return (
      <PageContainer>
        <PageHeader
          title="Nueva Orden de Compra"
          description="Selecciona el tipo de orden de compra"
          back={
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-9 text-muted-foreground"
              onClick={() => navigate("/purchasing/orders")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
          }
        />

        <div className="grid gap-4 md:grid-cols-3">
          <Card
            className="cursor-pointer transition-colors hover:border-primary"
            onClick={() => setOrderType("ingredients")}
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-100 p-2">
                  <Beaker className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle className="text-lg">Ingredientes</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Aceites y materias primas para produccion.
              </CardDescription>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer transition-colors hover:border-primary"
            onClick={() => setOrderType("supplies")}
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2">
                  <Box className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-lg">Misceláneos</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Materiales y Servicios no Stock.
              </CardDescription>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer transition-colors hover:border-primary"
            onClick={() => setOrderType("products")}
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg">Productos</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Productos terminados para reventa o inventario.
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Nueva Orden de Compra"
        description={
          orderType === "ingredients"
            ? "Aceites y materias primas"
            : orderType === "supplies"
              ? "Materiales y Servicios no Stock"
              : "Productos terminados"
        }
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => setOrderType(null)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Cambiar Tipo
          </Button>
        }
      />

      <PurchaseOrderForm
        orderType={orderType}
        onSubmit={handleSubmit}
        isSubmitting={createOrder.isPending}
      />
    </PageContainer>
  );
}
